import {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { portalLanguage } from '../i18n';
import {
  consentCookieValue,
  consentMode,
  deviceTimeZone,
  fetchGeo,
  needsConsentChoice,
  writeConsentCookie,
} from '../util/portalConsent';

/** The bar's height on <html>, so fixed bottom bars (the booking CTA) sit above it. */
export const CONSENT_HEIGHT_VAR = '--apr-consent-h';

type View = 'hidden' | 'ask' | 'notice';

/** The site's privacy policy in the reader's language. */
function privacyHref(): string {
  const lang = portalLanguage();
  return `https://aperiomobility.com${lang === 'fr' || lang === 'es' ? `/${lang}` : ''}/privacy`;
}

/**
 * The booking portal's consent bar (util/portalConsent), for a visitor with
 * no `apr_consent` decision on file - one who came straight to
 * book.aperiomobility.com. Ask-first regions get Accept all / Decline all
 * and nothing runs before a choice; elsewhere analytics and advertising
 * start at once (an implied grant, as on the site) and this notice says so,
 * with the same two buttons. Every choice is written to the cookie the site
 * shares, so neither host asks again for 12 months.
 */
export default function ConsentBar({
  onGranted,
  onDeclined,
}: {
  /** Analytics / advertising may start now (no reload). */
  onGranted: () => void;
  /** A grant made by the notice was withdrawn. */
  onDeclined: () => void;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('hidden');
  const granted = useRef(false);
  const bar = useRef<HTMLElement | null>(null);

  // The bar is fixed to the bottom of the screen, where the consumer skin's
  // booking button is on a phone (.apr-c-bar). It must never sit on top of
  // it: until 2026-10-24 the only way to reach "Continue" was to answer the
  // bar, and its first button is Decline all. Its height goes on <html> as
  // --apr-consent-h, which lifts that button (and the page's end) above it.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = bar.current;
    if (view === 'hidden' || !node) {
      root.style.removeProperty(CONSENT_HEIGHT_VAR);
      root.removeAttribute('data-apr-consent');
      return undefined;
    }
    const measure = () => {
      root.style.setProperty(CONSENT_HEIGHT_VAR, `${Math.ceil(node.getBoundingClientRect().height)}px`);
    };
    root.setAttribute('data-apr-consent', view);
    measure();
    const RO = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    const observer = RO ? new RO(measure) : null;
    observer?.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      root.style.removeProperty(CONSENT_HEIGHT_VAR);
      root.removeAttribute('data-apr-consent');
    };
  }, [view]);

  useEffect(() => {
    if (!needsConsentChoice()) return undefined;
    let live = true;
    fetchGeo().then((geo) => {
      if (!live || !needsConsentChoice()) return;
      if (consentMode(geo, deviceTimeZone()) === 'implied') {
        writeConsentCookie(consentCookieValue({ analytics: true, ads: true }, 'implied'));
        granted.current = true;
        onGranted();
        setView('notice');
      } else {
        setView('ask');
      }
    });
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (view === 'hidden') return null;

  const accept = () => {
    writeConsentCookie(consentCookieValue({ analytics: true, ads: true }, 'explicit'));
    if (!granted.current) {
      granted.current = true;
      onGranted();
    }
    setView('hidden');
  };
  const decline = () => {
    writeConsentCookie(consentCookieValue({ analytics: false, ads: false }, 'explicit'));
    if (granted.current) {
      granted.current = false;
      onDeclined();
    }
    setView('hidden');
  };

  const ask = view === 'ask';
  return (
    <section
      ref={bar}
      className="apr-consent"
      aria-label={t('consent.label')}
      data-consent-view={view}
    >
      <div className="apr-consent__text">
        {ask && <p className="apr-consent__title">{t('consent.title')}</p>}
        <p className="apr-consent__body">
          {ask ? t('consent.body') : t('consent.notice')}
          {' '}
          <a href={privacyHref()} target="_blank" rel="noopener noreferrer">
            {t('consent.privacy')}
          </a>
        </p>
      </div>
      <div className="apr-consent__actions">
        <button type="button" className="apr-consent__button" onClick={decline}>
          {t('consent.decline')}
        </button>
        <button
          type="button"
          className="apr-consent__button apr-consent__button--primary"
          onClick={accept}
        >
          {t('consent.accept')}
        </button>
        {!ask && (
          <button
            type="button"
            className="apr-consent__close"
            aria-label={t('consent.close')}
            onClick={() => setView('hidden')}
          >
            ×
          </button>
        )}
      </div>
    </section>
  );
}
